'use client';

import { useState } from 'react';
import Link from 'next/link';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from 'lucide-react';
import Image from 'next/image';
import { AnimatedContentBackground } from '@/components/AnimatedContentBackground';
import { useSeasonalLogo } from '@/hooks/useSeasonalLogo';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { seasonalLogo, themeColor, loading: logoLoading } = useSeasonalLogo();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });

      const data = await response.json();

      if (data.success) {
        setSent(true);
        toast.success('Check your inbox for a reset link.');
      } else {
        toast.error(data.error || 'Failed to send reset email');
      }
    } catch (error) {
      toast.error('Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black relative overflow-hidden">
      <AnimatedContentBackground />

      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Back Button */}
          <div className="mb-6">
            <Link
              href="/auth/signin"
              className="inline-flex items-center text-gray-400 hover:text-white transition-colors"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Sign In
            </Link>
          </div>

          {!sent ? (
            <>
              {/* Logo/Brand Section */}
              <div className="text-center mb-8">
                <div className="mb-4 h-[80px] flex items-center justify-center">
                  {!logoLoading && (
                    <Image
                      src={seasonalLogo?.url || "/image/epify.png"}
                      alt="Epify"
                      width={150}
                      height={150}
                      className="object-contain mx-auto"
                      style={{ maxHeight: '70px', width: 'auto', height: 'auto' }}
                      quality={100}
                      priority
                    />
                  )}
                </div>
                <h1 className="text-3xl font-bold text-white mb-2">Forgot Password?</h1>
                <p className="text-gray-400 text-sm">
                  No worries, we'll send you reset instructions
                </p>
              </div>

              {/* Form Card */}
              <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 shadow-2xl">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Email Field */}
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-gray-300 text-sm font-medium">
                      Email Address
                    </Label>
                    <div className="relative group theme-input-group">
                      <style>{`
                        .theme-input-group:focus-within .theme-icon {
                          color: ${themeColor} !important;
                        }
                        .theme-input:focus {
                          border-color: ${themeColor}80 !important;
                          box-shadow: 0 0 0 2px ${themeColor}1A !important;
                        }
                      `}</style>
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-5 w-5 text-gray-400 transition-colors theme-icon" />
                      </div>
                      <Input
                        id="email"
                        type="email"
                        autoComplete="email"
                        inputMode="email"
                        autoCapitalize="none"
                        spellCheck={false}
                        autoFocus
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        disabled={loading}
                        className="theme-input h-12 pl-10 bg-gray-800/50 border-gray-700/50 text-white placeholder-gray-500 rounded-xl transition-all"
                        placeholder="your@email.com"
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    disabled={loading || !email.trim()}
                    className="w-full h-12 text-white font-semibold transition-all"
                    style={{ backgroundColor: themeColor }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = `${themeColor}DD`;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = themeColor;
                    }}
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Reset Link'
                    )}
                  </Button>
                </form>
              </div>
            </>
          ) : (
            <>
              {/* Success State */}
              <div className="backdrop-blur-xl bg-black/40 border border-white/10 rounded-2xl p-8 shadow-2xl text-center">
                <div className="mb-6">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${themeColor}33` }}>
                    <CheckCircle2 className="w-8 h-8" style={{ color: themeColor }} />
                  </div>
                  <h2 className="text-2xl font-bold text-white mb-2">Check your email</h2>
                  <p className="text-gray-400 text-sm">
                    If an Epify account exists for<br />
                    <strong className="text-white break-all">{email}</strong>, we&apos;ve sent it a password reset link.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-black/30 rounded-lg border border-white/10">
                    <h3 className="text-sm font-semibold text-white mb-2">📧 Next steps</h3>
                    <ul className="text-xs text-gray-400 space-y-1 text-left">
                      <li>• Check your email inbox</li>
                      <li>• Click the reset link (expires in 1 hour)</li>
                      <li>• Create a new password</li>
                      <li>• Check spam folder if you don't see it</li>
                    </ul>
                  </div>

                  <Button
                    onClick={() => setSent(false)}
                    variant="outline"
                    className="w-full border-gray-700 hover:bg-gray-800"
                  >
                    <Mail className="w-4 h-4 mr-2" />
                    Send another email
                  </Button>

                  <Link
                    href="/auth/signin"
                    className="block text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    Back to Sign In
                  </Link>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
